/**
 * Scale reading for the POS weight screens.
 *
 * Tills on the legacy hardware controller answer `POST {imageUrl}getWeight`
 * with `{ Weight: "1.261 kg" }`. CW Browser has no such endpoint — port 8070 is
 * its payment bridge and answers 404 — and reads the scale over IPC instead
 * (`electronAPI.hardware.getWeightConfigured`).
 *
 * `requestScaleWeight` picks the right one and hands back an axios-shaped
 * response either way, so callers keep their existing parsing. Outside CW
 * Browser, `legacyRequest` runs exactly as before.
 */

const KG_PER_UNIT = { kg: 1, g: 0.001, lb: 0.45359237, oz: 0.028349523125 };

const browserScaleApi = () => {
  if (typeof window === "undefined") return null;
  const hardware = window.electronAPI?.hardware;
  return typeof hardware?.getWeightConfigured === "function" ? hardware : null;
};

/**
 * True when this POS is running inside CW Browser with a scale call available.
 * The call exists on every CW Browser install, scale or not: a PC with no scale
 * answers "No scale configured" (BUG-2662).
 */
export const hasBrowserScale = () => browserScaleApi() !== null;

// BUG-2662: the rejection carries why there is no reading (`scaleReason`, a reason of
// utils/weighingScale.js), so only a real failure reads as "Scale not responding".
const scaleError = (message, scaleReason) => Object.assign(new Error(message), { scaleReason });

/**
 * Read the scale CW Browser has configured and return it as `{ status, data: { Weight } }`.
 * Rejects — like a failed HTTP call would — when there is no usable reading:
 * no scale configured (noScale), port error (scale), overload (invalid), or a reading
 * that is still settling (unstable).
 */
export const readBrowserScaleWeight = async () => {
  const hardware = browserScaleApi();
  if (!hardware) throw scaleError("Scale not available", "scale");

  const result = await hardware.getWeightConfigured();
  if (!result?.success) {
    const error = result?.error || "Scale read failed";
    throw scaleError(error, /no scale configured/i.test(error) ? "noScale" : "scale");
  }
  if (result.status !== "stable") {
    const status = result.status || "unknown";
    // overload / parse_error: the scale answered with something unusable; unstable / motion: still settling.
    throw scaleError(`Scale reading not usable (${status})`, /over|error/i.test(status) ? "invalid" : "unstable");
  }

  const factor = KG_PER_UNIT[String(result.unit || "kg").toLowerCase()];
  const weight = Number(result.weight);
  if (!factor || !Number.isFinite(weight)) throw scaleError(`Unsupported scale reading: ${result.weight} ${result.unit}`, "invalid");

  // The callers strip a literal "kg", so the reading is always handed over in kg.
  return { status: 200, data: { Weight: `${Number((weight * factor).toFixed(3))} kg` } };
};

/**
 * @param {() => Promise<any>} legacyRequest the caller's original HTTP request, untouched
 * @returns {Promise<any>} an axios-shaped response
 */
export const requestScaleWeight = (legacyRequest) => (hasBrowserScale() ? readBrowserScaleWeight() : legacyRequest());
